import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { homeRoute } from '../services/homeRoute';
import { useAuth } from '../contexts/AuthContext';
import { useFirebaseAuth } from '../hooks/useFirebaseAuth';
import { isFirebaseAuthEnabled, isFirebaseConfigured } from '../config/firebase';
import Logo from '../components/Logo';
import {
  telegramAuthAPI,
  TelegramAuthData,
  phoneWaitlistAPI,
  logTelegramWidgetEvent,
} from '../services/api';
import TelegramLoginButton from '../components/auth/TelegramLoginButton';
import './Login.css';

// Note: country flags removed from the dropdown — <option> can't render
// SVG and the 🇪🇹/🇺🇸 emojis fall back to literal "ET"/"US" letters on
// many Linux/Windows builds. Plain "Ethiopia (+251)" reads cleanly on
// every device.

// During the v1 launch window (until Afromessage SMS is live ~Apr 22),
// the entire phone-login form is hidden on prod — students sign in with
// Google or Telegram only. Set REACT_APP_SHOW_PHONE_LOGIN=true (e.g. in
// .env.local) to expose the form for local dev or once SMS launches.
const SHOW_PHONE_LOGIN =
  (process.env.REACT_APP_SHOW_PHONE_LOGIN || 'false').toLowerCase() === 'true';

// When the phone form is shown, this controls whether submission goes to
// the waitlist endpoint vs the real OTP endpoint. Defaults to waitlist
// while SMS is not yet live. Independent of SHOW_PHONE_LOGIN.
const PHONE_WAITLIST_MODE =
  (process.env.REACT_APP_PHONE_WAITLIST_MODE || 'true').toLowerCase() === 'true';

const SMS_LAUNCH_DATE_LABEL =
  process.env.REACT_APP_SMS_LAUNCH_DATE_LABEL || 'Apr 22';

interface CountryOption {
  code: string;
  name: string;
  flag: string;
  placeholder: string;
  maxLength: number;
}

const COUNTRIES: CountryOption[] = [
  { code: '+251', name: 'Ethiopia', flag: '🇪🇹', placeholder: '9XXXXXXXX', maxLength: 9 },
  { code: '+1', name: 'United States', flag: '🇺🇸', placeholder: '(XXX) XXX-XXXX', maxLength: 10 },
];

// Telegram login is only shown on production domains (not localhost)
// The Telegram widget requires the domain to be registered with @BotFather
const isTelegramLoginEnabled = () => {
  const hostname = window.location.hostname;
  // Disable on localhost - Telegram widget requires registered domains
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return false;
  }
  // Check if bot name is configured
  const botName = process.env.REACT_APP_TELEGRAM_BOT_NAME || 'Lomistudybot';
  return !!botName;
};

const TELEGRAM_BOT_NAME = process.env.REACT_APP_TELEGRAM_BOT_NAME || 'Lomistudybot';

const Login: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+251');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState<'phone' | 'otp' | 'waitlist_done'>('phone');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [devOtp, setDevOtp] = useState<string | null>(null);
  const [waitlistMessage, setWaitlistMessage] = useState<string>('');

  const showTelegramLogin = isTelegramLoginEnabled();
  // Google sign-in is provided by Firebase Auth — show it whenever Firebase
  // is configured (the same env vars that gate phone OTP).
  const showGoogleLogin = isFirebaseConfigured();
  const selectedCountry = COUNTRIES.find(c => c.code === countryCode) || COUNTRIES[0];

  const navigate = useNavigate();
  const { login, loginWithFirebaseToken, refreshProfile, isAuthenticated, isLoading: authLoading } = useAuth();
  const firebaseAuth = useFirebaseAuth();
  const otpInputRef = useRef<HTMLInputElement | null>(null);

  // Redirect to dashboard if already logged in
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Check if the entered phone number is a test number (bypass Firebase entirely)
  const isTestPhone = (() => {
    const testNumbers = (process.env.REACT_APP_FIREBASE_TEST_PHONES || '').split(',').filter(Boolean);
    if (testNumbers.length === 0) return false;
    const cleaned = phoneNumber.replace(/\D/g, '');
    return testNumbers.some(tn => cleaned.endsWith(tn.trim()) || tn.trim().endsWith(cleaned));
  })();

  // Determine if using Firebase auth — test phones always use legacy path
  const useFirebase = !isTestPhone && isFirebaseAuthEnabled() && firebaseAuth.isConfigured;

  // Focus OTP input when switching to OTP step
  useEffect(() => {
    if (step === 'otp' && otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, [step]);

  const formatPhoneForDisplay = (value: string) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');
    return digits.slice(0, selectedCountry.maxLength);
  };

  const formatPhoneForAPI = (phone: string) => {
    let cleaned = phone.replace(/\D/g, '');
    // Remove leading 0 for Ethiopian numbers
    if (countryCode === '+251' && cleaned.startsWith('0')) {
      cleaned = cleaned.substring(1);
    }
    return countryCode + cleaned;
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setDevOtp(null);

    try {
      const formattedPhone = formatPhoneForAPI(phoneNumber);

      // Launch-window behavior: capture phone into waitlist instead of sending OTP.
      // SMS provider (Afromessage) isn't available yet — see docs/LAUNCH_CONTROL.md.
      if (PHONE_WAITLIST_MODE) {
        const result = await phoneWaitlistAPI.join(formattedPhone, countryCode, 'web');
        setWaitlistMessage(result.message);
        setStep('waitlist_done');
        return;
      }

      if (useFirebase) {
        // Firebase auth flow
        const success = await firebaseAuth.sendOTP(formattedPhone, 'send-otp-button');
        if (success) {
          setStep('otp');
        } else {
          setError(firebaseAuth.error || 'Failed to send OTP');
        }
      } else {
        // Legacy auth flow
        const response = await fetch('/api/auth/send-otp', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone_number: formattedPhone })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.detail || 'Failed to send OTP');
        }

        // Store dev OTP if returned (legacy mode only)
        if (data.otp_code) {
          setDevOtp(data.otp_code);
        }

        setStep('otp');
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || 'Failed to submit phone number');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (value: string) => {
    // Extract only digits and limit to 6
    const digits = value.replace(/\D/g, '').slice(0, 6);
    setOtp(digits);
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (otp.length !== 6) {
      setError('Please enter the complete 6-digit code');
      setLoading(false);
      return;
    }

    try {
      if (useFirebase) {
        // Firebase auth flow
        const result = await firebaseAuth.verifyOTP(otp);

        if (!result) {
          setError(firebaseAuth.error || 'Invalid code');
          setOtp('');
          otpInputRef.current?.focus();
          setLoading(false);
          return;
        }

        // Update auth context
        await loginWithFirebaseToken(result.accessToken, result.studentId);

        // Check if registration is needed
        if (result.requiresRegistration) {
          navigate('/register');
          return;
        }
      } else {
        // Legacy auth flow
        const formattedPhone = formatPhoneForAPI(phoneNumber);
        await login(formattedPhone, otp);

        // Check if user needs to complete registration
        const token = localStorage.getItem('access_token');
        if (token) {
          try {
            const response = await fetch('/api/student/profile', {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();

            // If stream or grade_level is null, user needs to complete registration
            if (!data.student?.stream || !data.student?.grade_level) {
              navigate('/register');
              return;
            }
          } catch (profileErr) {
            navigate('/register');
            return;
          }
        }
      }

      navigate(await homeRoute());
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Invalid code');
      // Clear OTP input on error
      setOtp('');
      otpInputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleChangePhone = () => {
    setStep('phone');
    setOtp('');
    setError('');
    setDevOtp(null);
  };

  // Google Sign-In via Firebase popup
  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      const result = await firebaseAuth.signInWithGoogle();

      if (!result) {
        // Hook already set the error message (or it was a silent close)
        if (firebaseAuth.error) setError(firebaseAuth.error);
        return;
      }

      // Hook already stored tokens in localStorage. Refresh AuthContext.
      await refreshProfile();

      // New users go to /register to complete profile, returning users to /dashboard
      if (result.isNewUser || result.requiresRegistration) {
        navigate('/register');
      } else {
        navigate(await homeRoute());
      }
    } catch (err: any) {
      setError(err?.message || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  // Telegram login handler
  const handleTelegramAuth = async (user: TelegramAuthData) => {
    console.log('handleTelegramAuth called with:', user);
    setError('');
    setLoading(true);

    try {
      console.log('Calling telegramAuthAPI.login...');
      logTelegramWidgetEvent('api_post_started', {
        telegram_id: user?.id,
        telegram_username: user?.username,
      });
      const response = await telegramAuthAPI.login(user);
      console.log('Telegram login response:', response);

      if (response.success && response.token) {
        console.log('Login successful, is_new_user:', response.is_new_user);

        // Update AuthContext state by refreshing profile
        console.log('Refreshing profile to update AuthContext...');
        await refreshProfile();
        console.log('Profile refreshed');

        // If new user, they may need to complete profile
        if (response.is_new_user) {
          console.log('Navigating to /register for new Telegram user');
          navigate('/register');
        } else {
          const dest = await homeRoute();
          console.log('Navigating to', dest);
          navigate(dest);
        }
      } else {
        console.log('Login failed:', response.message);
        setError(response.message || 'Telegram login failed');
      }
    } catch (err: any) {
      console.error('Telegram login error:', err);
      logTelegramWidgetEvent('api_post_failed', {
        telegram_id: user?.id,
        telegram_username: user?.username,
        error_status: err?.response?.status,
        error_message: err?.message || err?.response?.data?.detail || 'unknown',
      });
      setError(err.message || 'Telegram login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <Logo height={56} withAmharic />
          </div>
          <p className="login-tagline">Built to help you beat the ESSLCE &amp; the AAU UAT</p>
          <p className="login-subtitle">Your AI tutor for the exam that decides everything</p>
        </div>

        {step === 'phone' ? (
          <>
            {SHOW_PHONE_LOGIN && (
              <form onSubmit={handlePhoneSubmit}>
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <div className="input-group">
                    <select
                      className="country-select"
                      value={countryCode}
                      onChange={(e) => {
                        setCountryCode(e.target.value);
                        setPhoneNumber('');
                      }}
                      disabled={loading}
                    >
                      {COUNTRIES.map(country => (
                        <option key={country.code} value={country.code}>
                          {country.name} ({country.code})
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      id="phone"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(formatPhoneForDisplay(e.target.value))}
                      placeholder={selectedCountry.placeholder}
                      required
                      disabled={loading}
                      autoFocus
                    />
                  </div>
                  <small>
                    {PHONE_WAITLIST_MODE
                      ? `SMS verification launches ${SMS_LAUNCH_DATE_LABEL}. We'll text you the moment it's live.`
                      : `Enter your ${selectedCountry.name} phone number`}
                  </small>
                </div>

                {error && <div className="error-message">{error}</div>}

                <button
                  id="send-otp-button"
                  type="submit"
                  className="btn-primary"
                  disabled={loading || phoneNumber.length < selectedCountry.maxLength}
                >
                  {loading ? (
                    <>
                      <span className="spinner"></span>
                      {PHONE_WAITLIST_MODE ? 'Joining...' : 'Sending...'}
                    </>
                  ) : (
                    PHONE_WAITLIST_MODE ? 'Notify me when SMS launches' : 'Send Code'
                  )}
                </button>
              </form>
            )}

            {(showTelegramLogin || showGoogleLogin) && (
              <>
                {SHOW_PHONE_LOGIN && (
                  <div className="login-divider">
                    <span>or continue with</span>
                  </div>
                )}

                {!SHOW_PHONE_LOGIN && error && (
                  <div className="error-message">{error}</div>
                )}

                {showGoogleLogin && (
                  <button
                    type="button"
                    className="btn-google"
                    onClick={handleGoogleSignIn}
                    disabled={loading}
                  >
                    <svg className="btn-google__icon" viewBox="0 0 18 18" aria-hidden="true">
                      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                      <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
                      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
                    </svg>
                    Continue with Google
                  </button>
                )}

                {showTelegramLogin && (
                  <TelegramLoginButton
                    botName={TELEGRAM_BOT_NAME}
                    onAuth={handleTelegramAuth}
                    buttonSize="large"
                    cornerRadius={8}
                  />
                )}
              </>
            )}
          </>
        ) : step === 'waitlist_done' ? (
          <div className="phone-display">
            <p style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>✓</p>
            <p>{waitlistMessage || `You're on the list! We'll text you on ${SMS_LAUNCH_DATE_LABEL}.`}</p>
            <p className="phone-number">{countryCode}{phoneNumber}</p>
            <button
              type="button"
              className="btn-link"
              onClick={() => {
                setStep('phone');
                setPhoneNumber('');
                setWaitlistMessage('');
              }}
            >
              ← Use a different number
            </button>
            <div className="login-divider">
              <span>or sign in now with</span>
            </div>
            {showGoogleLogin && (
              <button
                type="button"
                className="btn-google"
                onClick={handleGoogleSignIn}
                disabled={loading}
              >
                <svg className="btn-google__icon" viewBox="0 0 18 18" aria-hidden="true">
                  <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                  <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                  <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
                  <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
                </svg>
                Continue with Google
              </button>
            )}
            {showTelegramLogin && (
              <TelegramLoginButton
                botName={TELEGRAM_BOT_NAME}
                onAuth={handleTelegramAuth}
                buttonSize="large"
                cornerRadius={8}
              />
            )}
          </div>
        ) : (
          <form onSubmit={handleVerifyOTP}>
            <div className="phone-display">
              <p>Enter the 6-digit code sent to</p>
              <p className="phone-number">{countryCode}{phoneNumber}</p>
            </div>

            <div className="otp-input-wrapper">
              <input
                ref={otpInputRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                className="otp-single-input"
                value={otp}
                onChange={(e) => handleOtpChange(e.target.value)}
                maxLength={6}
                disabled={loading}
                placeholder="······"
              />
              <div className="otp-boxes" aria-hidden="true">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className={`otp-box ${otp[i] ? 'filled' : ''} ${otp.length === i ? 'active' : ''}`}>
                    {otp[i] || ''}
                  </div>
                ))}
              </div>
            </div>

            {devOtp && process.env.NODE_ENV === 'development' && (
              <div className="dev-otp-display">
                <strong>Dev Mode:</strong> Your OTP is <code>{devOtp}</code>
              </div>
            )}

            {error && <div className="error-message">{error}</div>}

            <button
              type="submit"
              className="btn-primary"
              disabled={loading || otp.length !== 6}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Verifying...
                </>
              ) : (
                'Verify Code'
              )}
            </button>

            <button
              type="button"
              className="btn-link"
              onClick={handleChangePhone}
              disabled={loading}
            >
              ← Change phone number
            </button>
          </form>
        )}

        <div className="login-footer">
          <Link to="/">Learn more about Lomi →</Link>
        </div>

        <div className="telegram-promo">
          <svg className="telegram-icon" width="18" height="18" viewBox="0 0 24 24" fill="#0088cc" aria-hidden="true">
            <path d="M21.198 2.433a2.242 2.242 0 0 0-1.022.215l-16.5 7.5a2.25 2.25 0 0 0 .126 4.179l3.404 1.151 1.317 4.394c.135.451.539.762 1.005.762.281 0 .555-.116.756-.32l2.461-2.499 4.518 3.366c.305.226.679.347 1.062.347.198 0 .397-.033.587-.099a1.795 1.795 0 0 0 1.213-1.367l3.043-15.346a1.799 1.799 0 0 0-.575-1.668 1.794 1.794 0 0 0-1.395-.615z"/>
          </svg>
          <span>
            Also on Telegram —{' '}
            <a
              href="https://t.me/Lomistudybot"
              target="_blank"
              rel="noopener noreferrer"
              className="telegram-link"
            >
              @Lomistudybot
            </a>
          </span>
        </div>
      </div>
    </div>
  );
};

export default Login;
